import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { SectionHead, Reveal } from "@/components/Reveal";

const initials = (name) =>
  name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

export default function Testimonials() {
  const trackRef = useRef(null);
  const [items, setItems] = useState(null);
  const scroll = (dir) =>
    trackRef.current?.scrollBy({ left: dir * 440, behavior: "smooth" });

  useEffect(() => {
    api.get("/public/testimonials").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);

  return (
    <section data-testid="testimonials-section" className="bg-zinc-950 border-y border-zinc-800/70 py-24 sm:py-32 overflow-hidden">
      <div className="px-5 sm:px-10 lg:px-16 flex flex-wrap items-end justify-between gap-6 mb-12">
        <SectionHead overline="Training isn't only about lifting more — it's about moving better, feeling stronger, becoming more capable">
          Real people.
          <span className="block text-stroke">Real progress.</span>
        </SectionHead>
        {items?.length > 1 && (
          <div className="flex gap-2">
            <button data-testid="testimonials-prev" onClick={() => scroll(-1)} aria-label="Previous testimonials"
              className="border border-zinc-700 p-3 text-zinc-300 hover:border-volt hover:text-volt transition-colors">
              <ChevronLeft size={20} />
            </button>
            <button data-testid="testimonials-next" onClick={() => scroll(1)} aria-label="Next testimonials"
              className="border border-zinc-700 p-3 text-zinc-300 hover:border-volt hover:text-volt transition-colors">
              <ChevronRight size={20} />
            </button>
          </div>
        )}
      </div>

      <Reveal>
        {items === null ? null : items.length === 0 ? (
          <p className="px-5 sm:px-10 lg:px-16 text-zinc-500 text-sm" data-testid="testimonials-empty">
            Member stories land here as our first clients check in their progress.
          </p>
        ) : (
          <div ref={trackRef} data-testid="testimonials-track"
            className="flex gap-4 overflow-x-auto snap-x snap-mandatory no-scrollbar px-5 sm:px-10 lg:px-16 pb-2">
            {items.map((t) => (
              <blockquote key={t.id} data-testid={`testimonial-card-${t.id}`}
                className="snap-start shrink-0 w-[84vw] sm:w-[420px] border border-zinc-800 bg-ink p-8 sm:p-10 flex flex-col justify-between min-h-[280px] hover:border-volt/60 transition-colors duration-500">
                <p className="text-lg sm:text-xl leading-relaxed text-zinc-100">“{t.text}”</p>
                <footer className="mt-8 flex items-center gap-4">
                  <div className="w-11 h-11 rounded-full bg-volt/15 border border-volt/40 flex items-center justify-center text-xs font-extrabold text-volt">
                    {initials(t.client_name)}
                  </div>
                  <div>
                    <p className="text-sm font-bold text-zinc-200">{t.client_name}{t.age_range ? ` · ${t.age_range}` : ""}</p>
                    <p className="text-xs uppercase tracking-[0.14em] text-zinc-500">
                      {t.program || "Capable 360 member"}
                      {t.capability_improvement && <span className="text-volt"> · {t.capability_improvement}</span>}
                    </p>
                  </div>
                </footer>
              </blockquote>
            ))}
          </div>
        )}
      </Reveal>
    </section>
  );
}
